import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
} from "@mui/material";
import { useCallback, useState } from "react";
import { SearchTextBox } from "../SearchTextBox";
import { EmptyContainer } from "../ui/EmptyContainer";

const sortPredicate = (s1: string, s2: string) => {
  return s1.toLowerCase() > s2.toLowerCase() ? 1 : -1;
};

const UploadsListContainer = styled(List)({
  height: "400px",
  overflowY: "scroll",
});

const ListItemIconAll = styled(ListItemIcon)({
  minWidth: "0px",
});

type LaunchTestDialogProps = {
  exports: Array<string>;
  onClose: () => void;
  onTest: (testExports: string[], withNewDownloads: boolean) => void;
} & DialogProps;

const LaunchTestDialog = ({ exports, open, onClose, onTest, ...props }: LaunchTestDialogProps) => {
  const [selectedExports, setSelectedExports] = useState<Array<string>>(
    exports.length === 1 ? exports : JSON.parse(localStorage.getItem("selectedExports") || "[]")
  );
  const [prevExports, setPrevExports] = useState(exports);
  const [searchText, setSearchText] = useState<string>("");

  if (prevExports !== exports) {
    setPrevExports(exports);
    if (exports.length === 1) {
      setSelectedExports(exports);
    }
  }

  const isSelected = useCallback(
    (name: string) => {
      return selectedExports.some((e) => e === name);
    },
    [selectedExports]
  );

  const handleSelection = useCallback(
    (currentExport: string) => {
      setSelectedExports((prev) => {
        let newSelectedExports: Array<string> = [];
        let present = false;
        prev.forEach((e) => {
          if (e === currentExport) present = true;
          else if (e !== currentExport) {
            newSelectedExports.push(e);
          }
        });

        if (!present) newSelectedExports.push(currentExport);

        newSelectedExports = newSelectedExports.sort(sortPredicate);
        localStorage.setItem("selectedExports", JSON.stringify(newSelectedExports));

        return newSelectedExports;
      });
    },
    [setSelectedExports]
  );

  const allSelected = selectedExports.length === exports.length;

  const onOk = (withNewDownloads: boolean) => {
    onTest(selectedExports, withNewDownloads);
    setSearchText("");
    onClose();
  };

  const filteredExports = exports.sort(sortPredicate).filter((e) => e.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <Dialog
      open={open}
      onClose={() => {
        setSearchText("");
        onClose();
      }}
      {...props}
    >
      <DialogTitle variant="heading.four">Launch Test</DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            fontSize: 14,
          }}
        >
          Select uploads to test with
        </DialogContentText>

        <Stack direction="row" sx={{ width: "100%", mt: 1, alignItems: "center" }}>
          <SearchTextBox value={searchText} onChange={setSearchText} placeholder="Search upload" />
          <ListItemButton key="select-all" onClick={() => setSelectedExports(allSelected ? [] : exports)} disableRipple>
            <ListItemIconAll>
              <Checkbox checked={allSelected} tabIndex={-1} />
            </ListItemIconAll>
            <ListItemText primary="All" slotProps={{ primary: { style: { fontSize: 14 } } }} />
          </ListItemButton>
        </Stack>
        <Divider />
        <UploadsListContainer dense data-testid="export-list">
          {filteredExports.length > 0 ? (
            filteredExports.map((e) => (
              <ListItemButton key={e} onClick={() => handleSelection(e)} disableRipple data-testid="upload-list-item">
                <ListItemIcon>
                  <Checkbox checked={isSelected(e)} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText
                  primary={e}
                  slotProps={{ primary: { style: { fontSize: 14 } } }}
                  data-testid={`export-name-${e}`}
                />
              </ListItemButton>
            ))
          ) : (
            <EmptyContainer title="No uploads" small />
          )}
        </UploadsListContainer>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setSearchText("");
            onClose();
          }}
          variant="outlined"
          data-testid="cancel-btn"
        >
          Cancel
        </Button>
        <Button onClick={() => onOk(false)} data-testid="test-with-existing" disabled={selectedExports.length === 0}>
          Test with existing downloads
        </Button>
        <Button onClick={() => onOk(true)} data-testid="test-with-new" disabled={selectedExports.length === 0}>
          Test with new downloads
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LaunchTestDialog;
